
import { CheckCircle, Loader2 } from "lucide-react";
import { Progress } from "@/components/ui/progress";

export type ProcessingStage = 
  | "uploading"
  | "processing" 
  | "extracting" 
  | "analyzing" 
  | "generating"
  | "complete";

interface ProcessingStagesProps {
  currentStage: ProcessingStage;
  progress: number;
  isError?: boolean;
  errorMessage?: string;
}

export function ProcessingStages({ 
  currentStage, 
  progress, 
  isError = false,
  errorMessage 
}: ProcessingStagesProps) {
  // Define the stages and their display properties
  const stages = [
    { id: "uploading", label: "Uploading File", description: "Transferring your file to the server" },
    { id: "processing", label: "Processing Document", description: "Reading and parsing document content" },
    { id: "extracting", label: "Extracting Text", description: "Converting document content to plain text" },
    { id: "analyzing", label: "Analyzing Content", description: "Understanding the document context" },
    { id: "generating", label: "Generating Metadata", description: "Creating tags and summaries" },
    { id: "complete", label: "Process Complete", description: "Your document is ready" }
  ];

  // Get the current stage index for progress calculation
  const currentStageIndex = stages.findIndex(stage => stage.id === currentStage);
  
  // Calculate an overall progress value that accounts for both the stage and the within-stage progress
  const totalStages = stages.length - 1; // Last stage is "complete"
  const baseProgress = (currentStageIndex / totalStages) * 100;
  const stageProgress = (progress / 100) * (1 / totalStages) * 100;
  const overallProgress = currentStage === "complete" ? 100 : baseProgress + stageProgress;

  return (
    <div className="space-y-4 pt-3">
      {/* Overall progress bar */}
      <Progress value={overallProgress} className="h-2" />
      
      {/* Current stage indicator */}
      <div className="text-sm text-center font-medium">
        {isError ? (
          <div className="text-destructive">
            Error: {errorMessage || "Something went wrong during processing"}
          </div>
        ) : currentStage === "complete" ? (
          <div className="text-green-600 flex items-center justify-center gap-1.5">
            <CheckCircle className="h-4 w-4" />
            Process Complete
          </div>
        ) : (
          <div className="flex items-center justify-center gap-1.5">
            <Loader2 className="h-4 w-4 animate-spin" />
            {stages.find(stage => stage.id === currentStage)?.label}
          </div>
        )}
      </div>
      
      {/* Stage description */}
      {!isError && currentStage !== "complete" && (
        <p className="text-xs text-muted-foreground text-center">
          {stages.find(stage => stage.id === currentStage)?.description}
        </p>
      )}
      
      {/* Detailed stages display */}
      <div className="grid grid-cols-6 gap-1">
        {stages.map((stage, index) => {
          const isActive = stage.id === currentStage;
          const isCompleted = 
            currentStage === "complete" ? true : 
            stages.findIndex(s => s.id === currentStage) > index;
          
          return (
            <div 
              key={stage.id} 
              className={`relative flex flex-col items-center ${index === stages.length - 1 ? '' : 'after:content-[""] after:absolute after:top-3 after:h-[1px] after:w-full after:right-0 after:translate-x-1/2 after:bg-border'}`}
            >
              <div 
                className={`z-10 flex items-center justify-center w-6 h-6 rounded-full border ${
                  isActive ? "border-primary bg-primary/10" :
                  isCompleted ? "border-green-500 bg-green-100/80" :
                  "border-muted bg-background"
                }`}
              >
                {isCompleted ? (
                  <CheckCircle className="h-3.5 w-3.5 text-green-500" />
                ) : index === currentStageIndex && currentStage !== "complete" ? (
                  <Loader2 className="h-3.5 w-3.5 text-primary animate-spin" />
                ) : (
                  <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground/70" />
                )}
              </div>
              <span 
                className={`mt-1 text-[10px] font-medium ${
                  isActive ? "text-primary" :
                  isCompleted ? "text-green-600" :
                  "text-muted-foreground"
                }`}
              >
                {stage.label.split(' ')[0]}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
